import React, { useState, useCallback, useMemo } from 'react';
import { Box, Center, Spinner } from '@chakra-ui/react';
import { RequisitionFilters } from '@/components/shared/requisitions/filters/RequisitionFilters';
import { AssociateReqList } from '@/components/shared/requisitions/associate-req/components/AssociateReqList';
import { Requisition } from '@/components/shared/requisitions/associate-req/associateReq.interface';
import { useAssociateRequisitions } from '@/components/shared/requisitions/associate-req/api/associateReqApi';
import { useParams } from 'react-router-dom';
import { KSwitch } from '@/ui-library/components/molecules/KSwitch/KSwitch';
import { useSelector } from 'react-redux';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { getAtsJobCreate } from '@/store/atsJobCreate/atsJobCreateSelectors';

interface AssociateReqContainerProps {
  users: any[];
  selectedReqs: Requisition[];
  onSelectReq: (req: Requisition) => void;
  multiSelect?: boolean;
  setReqListState?: (reqs: Requisition[]) => void;
  offerId?: string;
  showGroupDialog?: number | null;
  onSelectAllGroup?: (req: Requisition) => void;
  onSelectSingle?: (req: Requisition) => void;
}

export const AssociateReqContainer: React.FC<AssociateReqContainerProps> = ({
  users,
  selectedReqs,
  onSelectReq,
  multiSelect = false,
  setReqListState,
  offerId,
  showGroupDialog,
  onSelectAllGroup,
  onSelectSingle,
}) => {
  const { jobId } = useParams();
  const currUser = useSelector(selectCurrUser);
  const { currentJob } = useSelector(getAtsJobCreate);
  const currentJobData = currentJob?.data || {};
  const [showOnlyAssociated, setShowOnlyAssociated] = useState(true);
  const isJobAssociated = showOnlyAssociated && jobId && !multiSelect;

  /* eslint-disable react-hooks/preserve-manual-memoization -- manual deps intentionally curated */
  const defaultFilters = useMemo(
    () =>
      [
        currUser?.id && {
          filter_by: { label: 'Recruitment partner', value: 'recruiter_ids' },
          filter_condition: { value: 'is', label: 'Include any' },
          filter_by_values: [currUser.id],
        },
      ].filter(Boolean),
    [currUser?.id]
  );
  /* eslint-enable react-hooks/preserve-manual-memoization */

  const [filters, setFilters] = useState<any[]>(
    !isJobAssociated ? defaultFilters : []
  );
  const [sort, setSort] = useState({
    element: 'created_at',
    order: 'desc' as 'asc' | 'desc',
    label: 'Created date (Descending)',
  });
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Transform filters to API params - no dependencies needed since it's a pure function
  const transformFilters = useCallback((filterValues: any[]) => {
    return filterValues.reduce((acc, filter) => {
      if (
        filter.filter_by &&
        filter.filter_condition &&
        filter.filter_by_values
      ) {
        const fieldName = filter.filter_by.value;
        const conditionType = filter.filter_condition.value;
        const fieldValues = filter.filter_by_values;

        acc[fieldName] = {
          [conditionType]: fieldValues,
        };
      }
      return acc;
    }, {});
  }, []);

  // Memoize transformed filters separately to avoid including transformFilters in apiParams dependencies
  const transformedFilters = useMemo(
    () => transformFilters(filters),
    [transformFilters, filters]
  );

  const apiParams = useMemo(() => {
    const filterParams = transformedFilters;

    let params: any = {};

    if (multiSelect && !params.status) {
      params.status = {
        is: [
          'approved',
          'created',
          'open',
          'draft',
          'pending_approval',
          'rejected',
        ],
      };
    }
    // Add ats_job_id if toggle is enabled
    if (isJobAssociated) {
      params.ats_job_id = jobId;
      params.status = {
        is: ['approved', 'created', 'open', 'filled'],
      };
    } else if (!multiSelect) {
      params = {
        ...params,
        ...filterParams,
      };
    } else {
      // Only exclude associated when not showing associated ones
      params.exclude_associated = true;
      params = {
        ...params,
        ...filterParams,
      };
    }

    if (!multiSelect && !params.status) {
      params.status = {
        is: ['approved', 'created', 'open', 'filled'],
      };
    }

    if (sort) {
      params.sort_key = sort.element;
      params.sort_order = sort.order;
    }

    if (searchTerm) {
      params.q = searchTerm;
    }

    return params;
  }, [
    transformedFilters,
    isJobAssociated,
    multiSelect,
    sort,
    searchTerm,
    jobId,
  ]);

  const {
    data: requisitionsData,
    isLoading,
    isFetching,
  } = useAssociateRequisitions(apiParams);

  // Filter requisitions for single select mode: only show requisitions with matching jobId or no ats_job
  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- manual deps intentionally curated
  const filteredRequisitions = useMemo(() => {
    if (multiSelect || !requisitionsData?.requisitions) {
      return requisitionsData?.requisitions || [];
    }

    const filtered = requisitionsData.requisitions.filter(
      (req: Requisition) => {
        const jobMatches =
          !req.ats_job || String(req.ats_job?.id) === String(jobId);
        const associationMatches =
          !req.requisition_association ||
          String(req.requisition_association?.offer?.id) === String(offerId);
        const isAtsApplication =
          req.requisition_association?.associable_type === 'AtsApplication';
        return jobMatches && associationMatches && !isAtsApplication;
      }
    );

    const sorted = [...filtered].sort((a: Requisition, b: Requisition) => {
      const aMatchesJob = a.ats_job && String(a.ats_job.id) === String(jobId);
      const bMatchesJob = b.ats_job && String(b.ats_job.id) === String(jobId);
      const aHasNoJob = !a.ats_job;
      const bHasNoJob = !b.ats_job;

      if (aMatchesJob && bMatchesJob) return 0;
      if (aHasNoJob && bHasNoJob) return 0;

      if (aMatchesJob) return -1;
      if (bMatchesJob) return 1;

      if (aHasNoJob) return -1;
      if (bHasNoJob) return 1;

      return 0;
    });

    return sorted;
  }, [requisitionsData?.requisitions, multiSelect, jobId, offerId]);

  const handleFiltersChange = useCallback((newFilters: any[]) => {
    setFilters(newFilters);
  }, []);

  const handleSortChange = useCallback((newSort: any) => {
    setSort(newSort);
  }, []);

  const handleSearchChange = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  const handleToggleChange = useCallback((isChecked: boolean) => {
    setShowOnlyAssociated(isChecked);
  }, []);

  return (
    <Box>
      <Box borderBottom="1px solid" borderColor="border-color">
        {!multiSelect && (
          <Box mb="12px" bg="secondary-25" p="7px 12px">
            <KSwitch
              isChecked={showOnlyAssociated}
              onChange={handleToggleChange}
              label={`Show requisitions associated with ${currentJobData?.name} only`}
              backgroundColor="primary-500"
              className={`ats-switch-toogle ${
                showOnlyAssociated ? 'active' : 'unactive'
              }`}
              customStyles={{
                margin: '0px',
              }}
            />
          </Box>
        )}
        <Box px="24px" pb="12px">
          <RequisitionFilters
            filters={filters}
            sort={sort}
            searchTerm={searchTerm}
            onFiltersChange={handleFiltersChange}
            onSortChange={handleSortChange}
            onSearchChange={handleSearchChange}
            associateOfferHireStatus={!multiSelect}
            associateJobStatus={multiSelect}
            disabled={!multiSelect ? showOnlyAssociated : false}
            filterIds={[
              'status',
              'department_ids',
              'office_ids',
              'employment_type',
              'recruiter_ids',
              'hiring_manager_ids',
            ]}
          />
        </Box>
      </Box>
      <Box height="400px" overflowY="auto">
        {isLoading || isFetching ? (
          <Center justifyContent="center" h="100%" w="100%">
            <Spinner color="primary-500" />
          </Center>
        ) : (
          <AssociateReqList
            requisitions={filteredRequisitions}
            selectedReqs={selectedReqs}
            users={users}
            onSelectReq={onSelectReq}
            setReqListState={setReqListState}
            offerId={offerId}
            jobAssociated={!multiSelect ? showOnlyAssociated : false}
            showGroupDialog={showGroupDialog}
            onSelectAllGroup={onSelectAllGroup}
            onSelectSingle={onSelectSingle}
          />
        )}
      </Box>
    </Box>
  );
};
